import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { articlesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import Spinner from "../components/Spinner";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import {
  BookOpen,
  Edit2,
  Copy,
  Trash2,
  Trash,
  Plus,
  X,
  PlusSquare,
  Save,
  FolderInput,
  Folder,
  StickyNote,
  Layers,
} from "lucide-react";
import { formatReadingEstimate } from "../utils/readingTime";
import "./StudyLessons.css";

const StudyLessons = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const folderFromUrl = searchParams.get("folder");
  const [articles, setArticles] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState(null);
  const [newName, setNewName] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const [showSubfolderCreate, setShowSubfolderCreate] = useState(null);
  const [newSubfolderName, setNewSubfolderName] = useState("");
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [movingLessonId, setMovingLessonId] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newCreateFolderName, setNewCreateFolderName] = useState("");
  const [savingLessonId, setSavingLessonId] = useState(null);
  const [savingFolderName, setSavingFolderName] = useState(null);
  const { addToast } = useToasts();

  const buildBreadcrumbs = React.useMemo(() => {
    const breadcrumbs = [{ label: "MySpace", path: "/myspace" }];

    if (folderFromUrl) {
      breadcrumbs.push({ label: "Classes", path: "/study/lessons" });
      const parts = folderFromUrl.split(" / ");
      parts.forEach((part, index) => {
        const path = parts.slice(0, index + 1).join(" / ");
        const isLast = index === parts.length - 1;
        breadcrumbs.push({
          label: part,
          path: isLast
            ? null
            : `/study/lessons?folder=${encodeURIComponent(path)}`,
        });
      });
    } else {
      breadcrumbs.push({ label: "Classes", path: null });
    }

    return breadcrumbs;
  }, [folderFromUrl]);

  const handleCreateLesson = async () => {
    try {
      const response = await articlesAPI.createArticle({
        title: folderFromUrl,
        author: "",
        chapter: "",
        body: " ",
        contentType: "lesson",
      });
      const newLessonId = response.data._id;
      navigate(
        `/view/lesson/${newLessonId}?folder=${encodeURIComponent(folderFromUrl)}&edit=true`,
      );
    } catch {
      addToast("Failed to create lesson", "error-warning");
    }
  };

  const handleCopy = async (article) => {
    try {
      await navigator.clipboard.writeText(article.body);
      addToast("Lesson copied to clipboard", "success-info");
    } catch {
      addToast("Failed to copy to clipboard", "error-warning");
    }
  };

  const handleDelete = async (article) => {
    const articleId = article._id;
    setDeletingId(null);
    setDeleteStep(0);

    try {
      await articlesAPI.deleteArticle(articleId);
      
      addToast("Lesson deleted successfully", "success-info");
      loadMyArticles();
    } catch {
      addToast("Failed to delete lesson", "error-warning");
    }
  };

  const handleRename = async (article) => {
    if (!newName.trim()) {
      addToast("Name cannot be empty", "error-warning");
      return;
    }
    try {
      setSavingLessonId(article._id);
      await articlesAPI.updateArticle(article._id, {
        title: article.title,
        author: article.author,
        chapter: newName.trim(),
        body: article.body,
      });
      
      addToast("Lesson renamed successfully", "success-info");
      setRenamingId(null);
      setNewName("");
      loadMyArticles();
    } catch {
      addToast("Failed to rename lesson", "error-warning");
    } finally {
      setSavingLessonId(null);
    }
  };

  const handleCreateFolder = async () => {
    if (!newCreateFolderName.trim()) {
      addToast("Folder name is required", "error-warning");
      return;
    }

    setShowCreateFolder(false);
    const folderName = newCreateFolderName;
    setNewCreateFolderName("");

    const toTitleCase = (str) =>
      str
        .trim()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    const finalFolderName = toTitleCase(folderName.trim());
    const fullPath = folderFromUrl
      ? `${folderFromUrl} / ${finalFolderName}`
      : finalFolderName;

    try {
      await articlesAPI.createArticle({
        title: fullPath,
        body: "Folder placeholder",
        contentType: "lesson",
      });
      
      addToast(`Folder "${finalFolderName}" created`, "success-info");
      setTimeout(async () => {
        setLoading(true);
        await loadMyArticles();
      }, 100);
    } catch {
      addToast("Failed to create folder", "error-warning");
    }
  };

  const handleCreateSubfolder = async (parentFolder) => {
    if (!newSubfolderName.trim()) {
      addToast("Subfolder name is required", "error-warning");
      return;
    }

    // Prevent double execution
    if (showSubfolderCreate !== parentFolder) {
      return;
    }

    // Clear state immediately to prevent double execution
    setShowSubfolderCreate(null);
    const subfolderName = newSubfolderName;
    setNewSubfolderName("");

    const toTitleCase = (str) =>
      str
        .trim()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    const finalSubfolderName = toTitleCase(subfolderName.trim());
    const fullPath = `${parentFolder} / ${finalSubfolderName}`;

    try {
      // Get all articles in parent folder
      const response = await articlesAPI.getArticlesByFolder(parentFolder);
      const articles = response.data.articles.filter(
        (a) =>
          a.contentType === "lesson" &&
          a.title === parentFolder &&
          a.body !== "Folder placeholder" &&
          a.body !== "Subfolder placeholder",
      );

      // Create subfolder placeholder
      await articlesAPI.createArticle({
        title: fullPath,
        body: "Subfolder placeholder",
        contentType: "lesson",
      });

      // Move only direct lessons from current folder to the new subfolder.
      // Do not move lessons that already belong to deeper subfolders.
      for (const article of articles) {
        const newTitle = fullPath;
        await articlesAPI.updateArticle(article._id, {
          title: newTitle,
          author: article.author,
          chapter: article.chapter,
          body: article.body,
        });
      }

      
      addToast(
        `Subfolder "${finalSubfolderName}" created${articles.length > 0 ? ` and ${articles.length} lessons moved` : ""}`,
        "success-info",
      );
      loadMyArticles();
    } catch {
      addToast("Failed to create subfolder", "error-warning");
    }
  };

  const handleRenameFolder = async (folderName) => {
    if (!newFolderName.trim()) {
      addToast("Folder name is required", "error-warning");
      return;
    }

    const toTitleCase = (str) =>
      str
        .trim()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    const finalFolderName = toTitleCase(newFolderName.trim());
    const parts = folderName.split(" / ");
    parts[parts.length - 1] = finalFolderName;
    const newFullPath = parts.join(" / ");

    try {
      setSavingFolderName(folderName);
      const response = await articlesAPI.getArticlesByFolder(folderName);
      const articles = response.data.articles;

      await Promise.all(
        articles.map((article) => {
          const newTitle =
            article.title === folderName
              ? newFullPath
              : article.title.replace(folderName, newFullPath);
          return articlesAPI.updateArticle(article._id, {
            title: newTitle,
            author: article.author,
            chapter: article.chapter,
            body: article.body,
          });
        }),
      );

      
      addToast(`Folder renamed to "${finalFolderName}"`, "success-info");
      setRenamingFolderId(null);
      setNewFolderName("");
      loadMyArticles();
    } catch {
      addToast("Failed to rename folder", "error-warning");
    } finally {
      setSavingFolderName(null);
    }
  };

  const handleCopyFolder = async (folderName) => {
    try {
      const response = await articlesAPI.getArticlesByFolder(folderName);
      const articles = response.data.articles.filter(
        (a) =>
          a.body !== "Folder placeholder" && a.body !== "Subfolder placeholder",
      );
      const allText = articles.map((a) => a.body).join("\n\n");
      await navigator.clipboard.writeText(allText);
      addToast(
        `Copied ${articles.length} lessons to clipboard`,
        "success-info",
      );
    } catch {
      addToast("Failed to copy lessons", "error-warning");
    }
  };

  const handleDeleteFolder = async (folderName) => {
    setDeletingId(null);
    setDeleteStep(0);

    try {
      const response = await articlesAPI.getArticlesByFolder(folderName);
      const articles = response.data.articles;
      const realCount = articles.filter(
        (a) => a.body !== "Folder placeholder" && a.body !== "Subfolder placeholder"
      ).length;

      for (const article of articles) {
        await articlesAPI.deleteArticle(article._id);
      }

      
      addToast(
        realCount > 0
          ? `Folder "${folderName.split(" / ").pop()}" and ${realCount} items deleted`
          : `Folder "${folderName.split(" / ").pop()}" deleted`,
        "success-info",
      );
      loadMyArticles();
    } catch {
      addToast("Failed to delete folder", "error-warning");
    }
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async (item, isFolder = false) => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }

    if (deleteStep === 2) {
      if (isFolder) {
        await handleDeleteFolder(item);
      } else {
        await handleDelete(item);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeletingId(null);
    setDeleteStep(0);
  };

  const handleMoveLessonClick = async (e, lessonId) => {
    e.stopPropagation();
    try {
      const response = await articlesAPI.getFolders("lesson");
      const allFolders = response.data.folders;

      // Filter to show only leaf folders (folders without subfolders) and exclude current folder
      const leafFolders = allFolders.filter((folder) => {
        const hasSubfolder = allFolders.some((f) =>
          f.name.startsWith(folder.name + " / "),
        );
        return !hasSubfolder && folder.name !== folderFromUrl;
      });

      setAvailableFolders(leafFolders);
      setMovingLessonId(lessonId);
    } catch {
      addToast("Failed to load folders", "error-warning");
    }
  };

  const handleMoveLesson = async (article, targetFolder) => {
    try {
      await articlesAPI.updateArticle(article._id, {
        title: targetFolder,
        author: article.author,
        chapter: article.chapter,
        body: article.body,
      });
      
      addToast(`Lesson moved to "${targetFolder}"`, "success-info");
      setMovingLessonId(null);
      setAvailableFolders([]);
      loadMyArticles();
    } catch {
      addToast("Failed to move lesson", "error-warning");
    }
  };

  const loadMyArticles = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await articlesAPI.getLessonsBrowser(folderFromUrl || "");
      const lessons = response.data.lessons || [];
      const browserSubfolders = response.data.subfolders || [];

      const mappedSubfolders = browserSubfolders.map((folder) => ({
        name: folder.path,
        isFolder: true,
        updatedAt: folder.updatedAt,
      }));

      setArticles([...lessons].reverse());
      setSubfolders(mappedSubfolders);

      setLoading(false);
    } catch {
      addToast("Failed to load lessons", "error-warning");
      setLoading(false);
    }
  }, [addToast, folderFromUrl]);

  useEffect(() => {
    loadMyArticles();
  }, [loadMyArticles]);

  if (loading) {
    return (
      <StudyLayout 
        breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
        buttons={<NavigationButtons />}
      >
        <Spinner visible={true} />
      </StudyLayout>
    );
  }

  if (articles.length === 0 && subfolders.length === 0) {
    return (
      <StudyLayout 
        breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
        buttons={<NavigationButtons />}
      >
        <div className="study-lessons-grid">
          {folderFromUrl ? (
            <div
              className="study-lesson-card study-create-card"
              onClick={handleCreateLesson}
              title="Create new lesson"
              style={{ position: "relative" }}
            >
              <BookOpen
                size={32}
                style={{
                  position: "absolute",
                  top: "12px",
                  left: "12px",
                  opacity: 0.6,
                }}
              />
              <Plus size={48} />
            </div>
          ) : (
            <div
              className="study-lesson-card study-create-card"
              style={{ position: "relative" }}
            >
              {!showCreateFolder ? (
                <div
                  onClick={() => setShowCreateFolder(true)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                  }}
                  title="Create new folder"
                >
                  <Folder
                    size={32}
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "12px",
                      opacity: 0.6,
                    }}
                  />
                  <Plus size={48} />
                </div>
              ) : (
                <>
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                    title="Create new folder"
                  >
                    <Folder
                      size={32}
                      style={{
                        position: "absolute",
                        top: "12px",
                        left: "12px",
                        opacity: 0.6,
                      }}
                    />
                    <Plus size={48} />
                  </div>
                  <div className="subfolder-create-in-card">
                    <div className="delete-confirmation-content">
                      <textarea
                        value={newCreateFolderName}
                        onChange={(e) => setNewCreateFolderName(e.target.value)}
                        placeholder="Folder name"
                        maxLength="50"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleCreateFolder();
                          }
                          if (e.key === "Escape") {
                            setShowCreateFolder(false);
                            setNewCreateFolderName("");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        rows="1"
                        className="folder-name-textarea"
                        style={{
                          width: "100%",
                          height: "auto",
                          minHeight: "1.2em",
                          resize: "none",
                          overflow: "hidden",
                          padding: "4px 6px",
                        }}
                        onInput={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                      />
                      <div
                        className="delete-actions"
                        style={{ marginTop: "8px" }}
                      >
                        <button
                          type="button"
                          className="create-confirm-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateFolder();
                          }}
                          title="Create"
                        >
                          <Plus size={14} />
                        </button>
                        <button
                          type="button"
                          className="delete-cancel-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCreateFolder(false);
                            setNewCreateFolderName("");
                          }}
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </StudyLayout>
    );
  }

  if (subfolders.length > 0 && (!folderFromUrl || articles.length === 0)) {
    return (
      <StudyLayout 
        breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
        buttons={<NavigationButtons />}
      >
        <div className="study-lessons-grid">
          {subfolders.map((subfolder) => (
            <div
              key={subfolder.name}
              className="study-lesson-card"
              onClick={() =>
                navigate(
                  `/study/lessons?folder=${encodeURIComponent(subfolder.name)}`,
                )
              }
            >
              <div className="study-lesson-icon">
                <Folder size={32} style={{ color: "#6b7280" }} />
              </div>
              <div className="study-lesson-name">
                {renamingFolderId === subfolder.name ? (
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRenameFolder(subfolder.name);
                      if (e.key === "Escape") {
                        setRenamingFolderId(null);
                        setNewFolderName("");
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="folder-name-input"
                  />
                ) : (
                  subfolder.name.split(" / ").pop()
                )}
              </div>
              <div
                className="study-lesson-info"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="study-lesson-count"></div>
                <div className="study-lesson-actions">
                  <button
                    className="study-lesson-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSubfolderCreate(subfolder.name);
                    }}
                    title="Create subfolder"
                  >
                    <PlusSquare size={18} />
                  </button>
                  <button
                    className={`study-lesson-btn ${renamingFolderId === subfolder.name ? "save-mode" : ""}`}
                    disabled={savingFolderName === subfolder.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (renamingFolderId === subfolder.name) {
                        handleRenameFolder(subfolder.name);
                      } else {
                        setRenamingFolderId(subfolder.name);
                        setNewFolderName(subfolder.name.split(" / ").pop());
                      }
                    }}
                    title={
                      renamingFolderId === subfolder.name
                        ? "Save changes"
                        : "Rename subfolder"
                    }
                  >
                    {renamingFolderId === subfolder.name ? (
                      savingFolderName === subfolder.name ? (
                        <span style={{ fontSize: "16px", lineHeight: 1 }}>...</span>
                      ) : (
                        <Save size={18} />
                      )
                    ) : (
                      <Edit2 size={18} />
                    )}
                  </button>
                  <button
                    className="study-lesson-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyFolder(subfolder.name);
                    }}
                    title="Copy all lessons"
                  >
                    <Copy size={18} />
                  </button>
                  <button
                    className="study-lesson-delete-btn"
                    onClick={(e) => handleDeleteClick(e, subfolder.name)}
                    title="Delete subfolder"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>

              {deleteStep > 0 && deletingId === subfolder.name && (
                <div
                  className="delete-confirmation-in-card"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="delete-confirmation-content">
                    <div className="delete-text">
                      {deleteStep === 1
                        ? `Delete folder and all inside?`
                        : "Delete everything? Cannot be undone!"}
                    </div>
                    <div className="delete-actions">
                      <button
                        type="button"
                        className="delete-confirm-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConfirm(subfolder.name, true);
                        }}
                        title={deleteStep === 1 ? "Delete" : "Yes, Delete"}
                      >
                        <Trash2 size={14} />
                      </button>
                      <button
                        type="button"
                        className="delete-cancel-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCancel();
                        }}
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showSubfolderCreate === subfolder.name && (
                <div
                  className="subfolder-create-in-card"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="delete-confirmation-content">
                    <textarea
                      value={newSubfolderName}
                      onChange={(e) => setNewSubfolderName(e.target.value)}
                      placeholder="Subfolder name"
                      maxLength="50"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateSubfolder(subfolder.name);
                        }
                        if (e.key === "Escape") {
                          setShowSubfolderCreate(null);
                          setNewSubfolderName("");
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      rows="1"
                      className="folder-name-textarea"
                      style={{
                        width: "100%",
                        height: "auto",
                        minHeight: "1.2em",
                        resize: "none",
                        overflow: "hidden",
                        padding: "4px 6px",
                      }}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                      }}
                    />
                    <div
                      className="delete-actions"
                      style={{ marginTop: "8px" }}
                    >
                      <button
                        type="button"
                        className="create-confirm-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateSubfolder(subfolder.name);
                        }}
                        title="Create"
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        type="button"
                        className="delete-cancel-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSubfolderCreate(null);
                          setNewSubfolderName("");
                        }}
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div
            className="study-lesson-card study-create-card"
            style={{ position: "relative" }}
          >
            {!showCreateFolder ? (
              <div
                onClick={() => setShowCreateFolder(true)}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
                title="Create new folder"
              >
                <Folder
                  size={32}
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "12px",
                    opacity: 0.6,
                  }}
                />
                <Plus size={48} />
              </div>
            ) : (
              <>
                <div
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                  }}
                  title="Create new folder"
                >
                  <Folder
                    size={32}
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "12px",
                      opacity: 0.6,
                    }}
                  />
                  <Plus size={48} />
                </div>
                <div className="subfolder-create-in-card">
                  <div className="delete-confirmation-content">
                    <textarea
                      value={newCreateFolderName}
                      onChange={(e) => setNewCreateFolderName(e.target.value)}
                      placeholder="Folder name"
                      maxLength="50"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateFolder();
                        }
                        if (e.key === "Escape") {
                          setShowCreateFolder(false);
                          setNewCreateFolderName("");
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      rows="1"
                      className="folder-name-textarea"
                      style={{
                        width: "100%",
                        height: "auto",
                        minHeight: "1.2em",
                        resize: "none",
                        overflow: "hidden",
                        padding: "4px 6px",
                      }}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                      }}
                    />
                    <div
                      className="delete-actions"
                      style={{ marginTop: "8px" }}
                    >
                      <button
                        type="button"
                        className="create-confirm-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateFolder();
                        }}
                        title="Create"
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        type="button"
                        className="delete-cancel-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCreateFolder(false);
                          setNewCreateFolderName("");
                        }}
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </StudyLayout>
    );
  }

  return (
    <StudyLayout 
      breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
      buttons={<NavigationButtons />}
    >
      <div className="study-lessons-grid">
        {articles.map((article, index) => (
          <div
            key={article._id}
            className="study-lesson-card"
            onClick={() =>
              navigate(
                `/view/lesson/${article._id}?folder=${encodeURIComponent(folderFromUrl)}`,
              )
            }
          >
            <div
              className="study-lesson-btn"
              style={{
                position: "absolute",
                top: "6px",
                left: "12px",
                cursor: "pointer",
                color: "#475569",
                padding: "4px",
                borderRadius: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/view/lesson/${article._id}?folder=${encodeURIComponent(folderFromUrl)}`
                );
              }}
              title="View lesson"
            >
              <BookOpen size={32} />
            </div>
            <div
              className="study-lesson-btn"
              style={{
                position: "absolute",
                top: "12px",
                left: "56px",
                cursor: "pointer",
                color: "#3b82f6",
                padding: "4px",
                borderRadius: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/study/notes?folder=${encodeURIComponent(folderFromUrl)}&type=note&lessonId=${article._id}`
                );
              }}
              title="View notes"
            >
              <StickyNote size={20} />
            </div>
            <div
              className="study-lesson-btn"
              style={{
                position: "absolute",
                top: "12px",
                left: "92px",
                cursor: "pointer",
                color: "#f59e0b",
                padding: "4px",
                borderRadius: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/study/flashcards?folder=${encodeURIComponent(folderFromUrl)}&lessonId=${article._id}`
                );
              }}
              title="View flashcards"
            >
              <Layers size={20} />
            </div>
            <div style={{ height: "32px" }}></div>
            <div>
              {renamingId === article._id ? (
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRename(article);
                    if (e.key === "Escape") {
                      setRenamingId(null);
                      setNewName("");
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  className="folder-name-input"
                />
              ) : (
                <div className="study-lesson-name">
                  {article.chapter || `Lesson ${index + 1}`}
                </div>
              )}
            </div>
            <div
              className="study-lesson-info"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="study-lesson-count">
                {formatReadingEstimate(article)}
              </div>
              <div className="study-lesson-actions">
                <button
                  className="study-lesson-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveLessonClick(e, article._id);
                  }}
                  title="Move lesson to another folder"
                >
                  <FolderInput size={18} />
                </button>
                <button
                  className={`study-lesson-btn ${renamingId === article._id ? "save-mode" : ""}`}
                  disabled={savingLessonId === article._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (renamingId === article._id) {
                      handleRename(article);
                    } else {
                      setRenamingId(article._id);
                      setNewName(article.chapter || `Lesson ${index + 1}`);
                    }
                  }}
                  title={
                    renamingId === article._id
                      ? "Save changes"
                      : "Rename lesson"
                  }
                >
                  {renamingId === article._id ? (
                    savingLessonId === article._id ? (
                      <span style={{ fontSize: "16px", lineHeight: 1 }}>...</span>
                    ) : (
                      <Save size={18} />
                    )
                  ) : (
                    <Edit2 size={18} />
                  )}
                </button>
                <button
                  className="study-lesson-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(article);
                  }}
                  title="Copy lesson"
                >
                  <Copy size={18} />
                </button>
                <button
                  className="study-lesson-delete-btn"
                  onClick={(e) => handleDeleteClick(e, article._id)}
                  title="Delete lesson"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>

            {deleteStep > 0 && deletingId === article._id && (
              <div
                className="delete-confirmation-in-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="delete-confirmation-content">
                  <div className="delete-text">
                    {deleteStep === 1
                      ? `Delete lesson?`
                      : "Delete? Cannot be undone!"}
                  </div>
                  <div className="delete-actions">
                    <button
                      type="button"
                      className="delete-confirm-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConfirm(article, false);
                      }}
                      title={deleteStep === 1 ? "Delete" : "Yes, Delete"}
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="delete-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCancel();
                      }}
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {movingLessonId === article._id && (
              <div
                className="subfolder-create-in-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="delete-confirmation-content"
                  style={{ gap: "4px" }}
                >
                  {availableFolders.length === 0 ? (
                    <div
                      style={{
                        padding: "8px",
                        fontSize: "0.75rem",
                        color: "var(--color-text-primary)",
                        textAlign: "center",
                      }}
                    >
                      No folders available to move lesson to.
                    </div>
                  ) : (
                    <div
                      style={{
                        maxHeight: "100px",
                        overflowY: "auto",
                        width: "100%",
                        fontSize: "0.75rem",
                      }}
                    >
                      {availableFolders.map((folder) => (
                        <div
                          key={folder.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveLesson(article, folder.name);
                          }}
                          style={{
                            padding: "4px 8px",
                            cursor: "pointer",
                            borderBottom: "1px solid var(--color-border)",
                            color: "var(--color-text-primary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.background = "var(--color-hover)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.background = "transparent")
                          }
                          title={folder.name}
                        >
                          {folder.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className="delete-cancel-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMovingLessonId(null);
                      setAvailableFolders([]);
                    }}
                    title="Cancel"
                    style={{ alignSelf: "center" }}
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <div
          className="study-lesson-card study-create-card"
          onClick={handleCreateLesson}
          title="Create new lesson"
          style={{ position: "relative" }}
        >
          <BookOpen
            size={32}
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              opacity: 0.6,
            }}
          />
          <Plus size={48} />
        </div>
      </div>
    </StudyLayout>
  );
};

export default StudyLessons;
